/**
 * Brand token derivation utilities.
 * Transforms partial BrandConfig into fully resolved DerivedBrandTokens.
 */
import { alpha, darken, lighten } from '@mui/material/styles';
// Re-export MUI's color utilities for convenience
export { alpha, darken, lighten };
// =============================================================================
// DEFAULT BRAND CONFIGURATION
// =============================================================================
/**
 * Default brand configuration values.
 * These are used when host doesn't provide specific values.
 */
export const defaultBrandConfig = {
    primaryColor: '#0C9FCB',
    textColor: '#1F2937',
    textColorSecondary: '#64748b',
    backgroundColor: '#ffffff',
    backgroundColorAlt: '#F8FAFC',
    utilityBarBackgroundColor: '#F8FAFC',
    utilityBarTextColor: '#1F2937',
    secondaryButtonColor: '#0C9FCB',
    activeTabColor: '#0C9FCB',
    audioWaveColor: '#0C9FCB',
    menuActiveColor: 'rgba(12, 159, 203, 0.08)',
    menuHoverColor: '#F8FAFC',
    errorColor: '#ef4444',
    successColor: '#10b981',
    warningColor: '#f59e0b',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSizeBase: '14px',
    borderRadius: '4px',
    brandName: 'Rheo AI Scribe',
};
// =============================================================================
// TOKEN DERIVATION
// =============================================================================
/**
 * Derive a complete set of brand tokens from a partial configuration.
 *
 * This function takes the minimal required inputs and generates all
 * the derived values (darker/lighter variants, computed colors, etc.)
 *
 * @param config Partial brand configuration from host
 * @returns Complete set of derived brand tokens
 */
export function deriveBrandTokens(config = {}) {
    // Merge with defaults for required values
    const primaryColor = config.primaryColor ?? defaultBrandConfig.primaryColor;
    const textColor = config.textColor ?? defaultBrandConfig.textColor;
    const backgroundColor = config.backgroundColor ?? defaultBrandConfig.backgroundColor;
    const errorColor = config.errorColor ?? defaultBrandConfig.errorColor;
    const successColor = config.successColor ?? defaultBrandConfig.successColor;
    const warningColor = config.warningColor ?? defaultBrandConfig.warningColor;
    const fontFamily = config.fontFamily ?? defaultBrandConfig.fontFamily;
    const fontSizeBase = config.fontSizeBase ?? defaultBrandConfig.fontSizeBase;
    const borderRadius = config.borderRadius ?? defaultBrandConfig.borderRadius;
    const brandName = config.brandName ?? defaultBrandConfig.brandName;
    // Derive color variants
    const primaryColorDark = config.primaryColorDark ?? darken(primaryColor, 0.15);
    const primaryColorLight = config.primaryColorLight ??
        (primaryColor === defaultBrandConfig.primaryColor
            ? '#86CADE'
            : lighten(primaryColor, 0.3));
    const textColorSecondary = config.textColorSecondary ??
        (textColor === defaultBrandConfig.textColor
            ? defaultBrandConfig.textColorSecondary
            : alpha(textColor, 0.7));
    const textColorMuted = config.textColorMuted ??
        (textColor === defaultBrandConfig.textColor
            ? '#9ca3af'
            : alpha(textColor, 0.38));
    const backgroundColorAlt = config.backgroundColorAlt ??
        (backgroundColor === defaultBrandConfig.backgroundColor
            ? defaultBrandConfig.backgroundColorAlt
            : darken(backgroundColor, 0.02));
    const backgroundColorSelected = config.backgroundColorSelected ?? alpha(primaryColor, 0.08);
    const utilityBarBackgroundColor = config.utilityBarBackgroundColor ?? backgroundColorAlt;
    const utilityBarTextColor = config.utilityBarTextColor ?? textColor;
    const secondaryButtonColor = config.secondaryButtonColor ?? primaryColor;
    const activeTabColor = config.activeTabColor ?? primaryColor;
    const audioWaveColor = config.audioWaveColor ?? primaryColor;
    const menuActiveColor = config.menuActiveColor ?? backgroundColorSelected;
    const menuHoverColor = config.menuHoverColor ?? backgroundColorAlt;
    // Derive border radius large (1.5x base)
    const borderRadiusValue = parseInt(borderRadius, 10) || 8;
    const borderRadiusLarge = config.borderRadiusLarge ?? `${Math.round(borderRadiusValue * 1.5)}px`;
    // Computed values (always derived, never passed directly)
    const primaryColorHover = alpha(primaryColor, 0.04);
    const backgroundHover = alpha(primaryColor, 0.04);
    const dividerColor = alpha(textColor, 0.12);
    return {
        // Core colors
        primaryColor,
        primaryColorDark,
        primaryColorLight,
        textColor,
        textColorSecondary,
        textColorMuted,
        backgroundColor,
        backgroundColorAlt,
        backgroundColorSelected,
        utilityBarBackgroundColor,
        utilityBarTextColor,
        secondaryButtonColor,
        activeTabColor,
        audioWaveColor,
        menuActiveColor,
        menuHoverColor,
        // Status colors
        errorColor,
        successColor,
        warningColor,
        // Typography
        fontFamily,
        fontSizeBase,
        // Shape
        borderRadius,
        borderRadiusLarge,
        // Computed
        primaryColorHover,
        backgroundHover,
        dividerColor,
        // Branding
        brandName,
    };
}
// =============================================================================
// WIDGET THEME BRIDGE
// =============================================================================
/**
 * Convert DerivedBrandTokens to the shell's WidgetTheme format.
 *
 * This bridges the new brand system with the existing widget shell theming.
 * The shell only uses a subset of properties for its chrome.
 *
 * @param brand Derived brand tokens
 * @returns WidgetTheme for the shell
 */
export function brandToWidgetTheme(brand) {
    return {
        primaryColor: brand.primaryColor,
        secondaryColor: brand.utilityBarBackgroundColor,
        textColor: brand.utilityBarTextColor,
        backgroundColor: brand.backgroundColor,
        fontFamily: brand.fontFamily,
        fontSize: brand.fontSizeBase,
        borderRadius: brand.borderRadius,
    };
}
//# sourceMappingURL=brandUtils.js.map